import { capitalize, get } from 'lodash';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getTypeUrl } from 'site-modules/shared/utils/type-link-constructor';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getVideoSchema } from 'client/seo/schemas/video';
import {
  getBreadcrumbName,
  TYPE_TITLE_LOWERCASE_EXCEPTIONS_MAPPING,
  getUserFriendlyTypeName,
} from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getModelState } from 'client/data/luckdragon/model';
import { CmsModel } from 'client/data/models/cms';
import { parseContent } from 'client/data/cms/content';
import { TypeRankingsModel, buildTypeRankingsPath } from 'client/data/models/type-rankings';
import { getRankingsContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { getCurrentDate } from 'client/site-modules/shared/utils/date-utils';
import { getPlural } from 'client/utils/plural';

import { getTypeFaqsContent } from './type-faqs-generator';
import { getSubtypeDisplayTitle } from './subtype-display';

function buildTypeBreadcrumbs(type) {
  return [
    getHomeBreadcrumb(),
    {
      href: getTypeUrl(type),
      title: getBreadcrumbName(type),
    },
  ];
}

const TYPE_IMAGE_MAP = {
  suv: {
    url: 'https://static.ed.edmunds-media.com/unversioned/img/edmunds-top-selling-suv-infographic.gif',
    width: 1010,
    height: 701,
    alt: 'Top Selling SUVs',
  },
  truck: {
    url: 'https://static.ed.edmunds-media.com/unversioned/img/edmunds-top-selling-trucks-infographic.gif',
    width: 1012,
    height: 689,
    alt: 'Top Selling Trucks',
  },
};

export function getCustomTitle(type) {
  const currentYear = getCurrentDate().getFullYear();
  const nextYear = currentYear + 1;

  return `Best ${getPlural(
    getUserFriendlyTypeName(type),
    undefined,
    undefined,
    true
  )} of ${currentYear} and ${nextYear}`;
}

export function getFormattedSubtypeName(subtype) {
  const firstAlphabetRegex = '\\b[a-zA-Z]';
  const firstSubtypeAlphabetIndex = subtype.search(firstAlphabetRegex);
  return subtype.substring(0, firstSubtypeAlphabetIndex) + capitalize(subtype.substring(firstSubtypeAlphabetIndex));
}

export function getCustomSubtypeTitle(type, subtype, subtypeExpanisonName) {
  const currentYear = getCurrentDate().getFullYear();
  const nextYear = currentYear + 1;

  return `Best ${
    subtypeExpanisonName || (type && subtype ? getSubtypeDisplayTitle(type, subtype) : '')
  } of ${currentYear} and ${nextYear}`;
}

function getCustomDescription(type, enableMarketBrewMetaDescriptionTest) {
  const currentYear = getCurrentDate().getFullYear();
  const nextYear = currentYear + 1;
  const pluralType = getPlural(
    TYPE_TITLE_LOWERCASE_EXCEPTIONS_MAPPING[type] || getUserFriendlyTypeName(type).toLowerCase()
  );
  return type === 'suv' && enableMarketBrewMetaDescriptionTest
    ? `Edmunds' expert reviewers rank the best ${pluralType} for ${currentYear} on a 10-point scale based on performance, comfort, interior, technology, and value.`
    : `Edmunds expert reviewers rank the best ${pluralType} of ${currentYear} and ${nextYear} on a 10-point scale that includes performance, comfort, interior, technology, and value.`;
}

function getSubtypeCustomDescription(type, subtype) {
  const currentYear = getCurrentDate().getFullYear();
  const nextYear = currentYear + 1;
  return `The best ${getSubtypeDisplayTitle(
    type,
    subtype,
    true,
    true
  )} of ${currentYear} and ${nextYear} ranked by experts. Get ratings, fuel economy, price and more. Find the best vehicle for you quickly and easily.`;
}

export const defineSeoContent = (state, props, type, subtype, bestRankings) => {
  if (!type) {
    return {};
  }
  const modelState = getModelState(state);
  const typeContent = parseContent(modelState.get(`content["${getRankingsContentPath(type)}"]`, CmsModel));
  const typeRanking = bestRankings ? [bestRankings] : modelState.get(buildTypeRankingsPath(type), TypeRankingsModel);
  const subtypeExpanisonName = get(bestRankings, 'displayName');
  const titleHeading = subtype ? getCustomSubtypeTitle(type, subtype, subtypeExpanisonName) : getCustomTitle(type);
  const title = `${titleHeading} - Expert Reviews and Rankings | Edmunds`;
  const enableMarketBrewMetaDescriptionTest = !!get(state, `featureFlags["enable-market-brew-test-meta-description"]`);
  const description = subtype
    ? getSubtypeCustomDescription(type, subtype)
    : getCustomDescription(type, enableMarketBrewMetaDescriptionTest);
  const videos = typeContent
    .child('video')
    .children()
    .map(child => child.getAllMetadata());
  const faqContent = typeContent.child('faq');

  const breadcrumbSchema = getBreadcrumbSchema(buildTypeBreadcrumbs(type));
  if (subtype && (props.location.pathname === `/${type}/${subtype}/` || bestRankings)) {
    const subtypeName = getFormattedSubtypeName(subtype);
    const numListItems = breadcrumbSchema.itemListElement.length;
    breadcrumbSchema.itemListElement.push({
      '@type': 'ListItem',
      position: numListItems + 1,
      item: {
        name: subtypeName,
      },
    });
  }
  const jsonld = [breadcrumbSchema];

  if (videos && videos.length) {
    jsonld.push(getVideoSchema(videos[0]));
  }

  const faqEntries = getTypeFaqsContent({ vehicleEditorialRankings: typeRanking, faqContent, typeSlug: type });

  if (faqEntries && faqEntries.length) {
    jsonld.push(
      getFAQSchema({
        entries: faqEntries,
        pathname: props.location.pathname,
        withTracking: true,
      })
    );
  }

  return {
    title,
    description,
    keywords: typeContent.title,
    jsonld,
    image: TYPE_IMAGE_MAP[type],
  };
};
